/**
 * Tenant API functions
 */
import apiService from "~/services/service";
import { TenantRoutes } from "./routes";
import type {
  CreateTenantRequest,
  CreateTenantResponse,
  VerifyDomainAvailabilityResponse,
} from "./types";
import {
  createTenantBodySchema,
  verifyDomainAvailabilityQuerySchema,
} from "./schemas";
import type { ApiResponse } from "~/lib/react-query/types";
import {
  CustomerAdminFormData,
  TenantInfoFormData,
} from "~/Pages/Tenants/onboarding/utils/formatters";
import API_ROUTES_V2 from "~/api-routes-v2";
import { getCountryISO2From3CharCode } from "~/utils/countries";
import { parse } from "date-fns";

export interface TenantDetailsResponse extends TenantInfoFormData {
  customer_admin?: CustomerAdminFormData | null;
  account_manager?: {
    id?: string;
    given_name?: string;
    surname?: string;
    other_name?: string;
    preferred_name?: string;
    email?: string;
    country?: string;
    phone_code?: string;
    phone_number?: string;
  } | null;
}

/**
 * Format Date to DD/MM/YYYY string
 */
function formatDateToDDMMYYYY(date: Date): string {
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const year = date.getUTCFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * Create a new tenant
 * Note: The schema transforms date strings to Date objects for validation,
 * but the API expects DD/MM/YYYY strings, so we convert back
 */
export async function createTenant(
  data: CreateTenantRequest,
): Promise<ApiResponse<CreateTenantResponse>> {
  // Validate request body with Zod schema
  // The schema will transform DD/MM/YYYY strings to Date objects
  const validatedData = createTenantBodySchema.parse(data);

  // Convert Date objects back to DD/MM/YYYY format strings for API
  // Note: billing_end_date can be null/undefined (optional field)
  const requestBody: CreateTenantRequest = {
    tenant_name: validatedData.tenant_name,
    type: validatedData.type,
    demo_tenant: validatedData.demo_tenant,
    account_name: validatedData.account_name,
    domains: validatedData.domains,
    total_number_of_users: validatedData.total_number_of_users,
    salesforce_id: validatedData.salesforce_id,
    country: validatedData.country,
    status: validatedData.status,
    billing_start_date: formatDateToDDMMYYYY(validatedData.billing_start_date),
    billing_end_date:
      validatedData.billing_end_date &&
      validatedData.billing_end_date instanceof Date
        ? formatDateToDDMMYYYY(validatedData.billing_end_date)
        : null,
    organization_id: validatedData.organization_id,
    customer_admin: validatedData.customer_admin,
    // Include Local Team specific fields if present
    ...(validatedData.supports_payroll_plus !== undefined && {
      supports_payroll_plus: validatedData.supports_payroll_plus,
    }),
    ...(validatedData.payslip_portal_link !== undefined && {
      payslip_portal_link: validatedData.payslip_portal_link,
    }),
    // Include account manager ID if present
    ...(data.account_manager_id && {
      account_manager_id: data.account_manager_id,
    }),
  };

  return apiService<ApiResponse<CreateTenantResponse>>(
    TenantRoutes.createTenant,
    "POST",
    {
      body: requestBody,
    },
  );
}

/**
 * Verify domain availability
 */
export async function verifyDomainAvailability(
  domains: string[],
): Promise<ApiResponse<VerifyDomainAvailabilityResponse>> {
  // Validate query params
  const validatedParams = verifyDomainAvailabilityQuerySchema.parse({
    domains,
  });

  // Build query string with multiple domain parameters
  const queryParams = new URLSearchParams();
  validatedParams.domains.forEach((domain) => {
    queryParams.append("domains", domain);
  });

  const url = `${TenantRoutes.verifyDomain}?${queryParams.toString()}`;

  return apiService<ApiResponse<VerifyDomainAvailabilityResponse>>(url, "GET");
}

export const getTenant = async (
  tenantId: string,
): Promise<TenantDetailsResponse | null> => {
  const response = await apiService(API_ROUTES_V2.FETCH_TENANT, "GET", {
    replace: { id: tenantId },
  });

  const api = response?.data;
  if (!api) return null;

  const formatted: TenantDetailsResponse = {
    tenant_name: api.tenant_name || "",
    type: api.type || "CUSTOMER",
    demo_tenant: api.demo_tenant ?? false,
    account_name: api.account_name || "",
    domains: api.domains || [],
    total_number_of_users: api.total_number_of_users || "",
    salesforce_id: api.salesforce_id || "",
    country:
      api.country && api.country.length === 3
        ? getCountryISO2From3CharCode(api.country) || api.country
        : api.country || "",
    status: api.status || "ACTIVE",
    billing_start_date: api.billing_start_date
      ? new Date(api.billing_start_date)
      : null,
    billing_end_date: api.billing_end_date
      ? new Date(api.billing_end_date)
      : null,
    ...(api.supports_payroll_plus !== undefined && {
      supports_payroll_plus: api.supports_payroll_plus,
    }),
    payslip_portal_link: api.payslip_portal_link ? api.payslip_portal_link : "",
  };

  if (api.customer_admin) {
    const adminCountry =
      api.customer_admin.country && api.customer_admin.country.length === 3
        ? getCountryISO2From3CharCode(api.customer_admin.country) ||
          api.customer_admin.country
        : api.customer_admin.country;

    const customerAdmin: CustomerAdminFormData = {
      given_name: api.customer_admin.given_name || "",
      surname: api.customer_admin.surname || "",
      other_name: api.customer_admin.other_name || "",
      preferred_name: api.customer_admin.preferred_name || "",
      contact_email: api.customer_admin.contact_email || "",
      country: adminCountry || "",
      phone_code: api.customer_admin.phone_code || "",
      phone_number: api.customer_admin.phone_number || "",
    };

    formatted.customer_admin = customerAdmin;
  }

  if (api.account_manager) {
    formatted.account_manager = {
      id: api.account_manager.id,
      given_name: api.account_manager.given_name,
      surname: api.account_manager.surname,
      other_name: api.account_manager.other_name,
      preferred_name: api.account_manager.preferred_name,
      email: api.account_manager.email,
      country: api.account_manager.country,
      phone_code: api.account_manager.phone_code,
      phone_number: api.account_manager.phone_number,
    };
  }

  return formatted;
};

export interface TenantAuditEntry {
  id: string;
  action: "create" | "update" | "delete";
  object_name: string | null;
  actor_name: string;
  actor_email: string;
  timestamp: string; // "25 Apr 2025 | 15:37:12" format
  old_data: Record<string, unknown> | null;
  new_data: Record<string, unknown> | null;
}

export interface TenantAuditApiResponse {
  success: boolean;
  message: string;
  data: {
    data: TenantAuditEntry[];
    pagination: {
      page: number;
      pageSize: number;
      total: number;
      totalPages: number;
    };
  };
  statusCode: number;
  status: boolean;
}

export interface TenantAuditResponse {
  success: boolean;
  message: string;
  data: TenantAuditEntry[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Fetch tenant audit logs with pagination
 */
export async function getTenantAudit(
  tenantId: string,
  page = 1,
  pageSize = 20,
): Promise<TenantAuditResponse> {
  // Build URL with pageSize as query parameter
  const baseUrl = API_ROUTES_V2.FETCH_TENANT_AUDIT.replace(
    "{id}",
    tenantId,
  ).replace("{page}", page.toString());
  const url = `${baseUrl}&pageSize=${pageSize}`;

  const response = await apiService<TenantAuditApiResponse>(url, "GET", {
    replace: {
      id: tenantId,
      page: page.toString(),
    },
  });

  // Extract the nested data structure
  const auditEntries = response.data?.data || [];
  const pagination = response.data?.pagination || {
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0,
  };

  // Normalize the response data structure for StepCard compatibility
  const normalizedData = auditEntries.map((item) => {
    // Parse the timestamp string "30 Dec 2025 | 18:17:34" to a Date object
    // Format: "d MMM yyyy | HH:mm:ss"
    let parsedTimestamp: Date | string = item.timestamp;
    try {
      // Try to parse the timestamp format "30 Dec 2025 | 18:17:34"
      const [datePart, timePart] = item.timestamp.split(" | ");
      if (datePart && timePart) {
        // Combine date and time parts: "30 Dec 2025 18:17:34"
        const dateTimeString = `${datePart} ${timePart}`;
        // Use date-fns parse for consistent cross-browser parsing
        // Format: "d MMM yyyy HH:mm:ss" (e.g., "2 Jan 2026 08:22:55")
        const parsedDate = parse(
          dateTimeString,
          "d MMM yyyy HH:mm:ss",
          new Date(),
        );
        // Check if the date is valid
        if (!Number.isNaN(parsedDate.getTime())) {
          parsedTimestamp = parsedDate;
        } else {
          // If parsing fails, log warning
          console.warn(
            "Failed to parse timestamp, using original:",
            item.timestamp,
          );
        }
      }
    } catch (error) {
      // If parsing fails, keep the original string
      // StepCard's formatDate will handle string timestamps
      console.warn("Failed to parse timestamp:", item.timestamp, error);
    }

    // Use old_data and new_data directly from API response
    // They are already objects with field keys as properties
    const normalizedOldData: Record<string, unknown> | null =
      item.old_data && Object.keys(item.old_data).length > 0
        ? item.old_data
        : null;
    const normalizedNewData: Record<string, unknown> | null =
      item.new_data && Object.keys(item.new_data).length > 0
        ? item.new_data
        : null;

    return {
      ...item,
      old_data: normalizedOldData,
      new_data: normalizedNewData,
      name: item.object_name || null,
      updatedAt: parsedTimestamp, // Map timestamp to updatedAt for StepCard
    };
  });

  return {
    success: response.success,
    message: response.message,
    data: normalizedData,
    pagination,
  };
}

/**
 * Raw API response structure for tenant entity access audit
 */
interface TenantEntityAccessAuditApiEntry {
  id: string;
  action:
    | "entity_access_added"
    | "entity_access_updated"
    | "entity_access_removed";
  entity_id: string;
  entity_name: string;
  actor_name: string;
  actor_email: string;
  timestamp: string;
  old_value: Record<string, unknown> | null;
  new_value: Record<string, unknown> | null;
}

interface TenantEntityAccessAuditApiResponse {
  success: boolean;
  message: string;
  data: {
    data: TenantEntityAccessAuditApiEntry[];
    pagination: {
      page: number;
      pageSize: number;
      total: number;
      totalPages: number;
    };
  };
  statusCode: number;
  status: boolean;
}

/**
 * Fetch tenant entity access audit logs with pagination
 */
export async function getTenantEntityAccessAudit(
  tenantId: string,
  page = 1,
  pageSize = 20,
): Promise<TenantAuditResponse> {
  // Build URL with pageSize as query parameter
  const baseUrl = API_ROUTES_V2.FETCH_TENANT_ENTITY_ACCESS_AUDIT.replace(
    "{id}",
    tenantId,
  ).replace("{page}", page.toString());
  const url = `${baseUrl}&pageSize=${pageSize}`;

  const response = await apiService<TenantEntityAccessAuditApiResponse>(
    url,
    "GET",
    {
      replace: {
        id: tenantId,
        page: page.toString(),
      },
    },
  );

  // Extract the nested data structure
  const auditEntries = response.data?.data || [];
  const pagination = response.data?.pagination || {
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0,
  };

  // Map action values from API format to standard format
  const mapAction = (action: string): "create" | "update" | "delete" => {
    if (action === "entity_access_added") return "create";
    if (action === "entity_access_updated") return "update";
    if (action === "entity_access_removed") return "delete";
    // Fallback to update if unknown action
    return "update";
  };

  // Normalize the response data structure for StepCard compatibility
  const normalizedData = auditEntries.map((item) => {
    // Parse the timestamp string "6 Jan 2026 | 13:24:05" to a Date object
    // Format: "d MMM yyyy | HH:mm:ss"
    let parsedTimestamp: Date | string = item.timestamp;
    try {
      // Try to parse the timestamp format "6 Jan 2026 | 13:24:05"
      const [datePart, timePart] = item.timestamp.split(" | ");
      if (datePart && timePart) {
        // Combine date and time parts: "6 Jan 2026 13:24:05"
        const dateTimeString = `${datePart} ${timePart}`;
        // Use date-fns parse for consistent cross-browser parsing
        // Format: "d MMM yyyy HH:mm:ss" (e.g., "6 Jan 2026 13:24:05")
        const parsedDate = parse(
          dateTimeString,
          "d MMM yyyy HH:mm:ss",
          new Date(),
        );
        // Check if the date is valid
        if (!Number.isNaN(parsedDate.getTime())) {
          parsedTimestamp = parsedDate;
        } else {
          // If parsing fails, log warning
          console.warn(
            "Failed to parse timestamp, using original:",
            item.timestamp,
          );
        }
      }
    } catch (error) {
      // If parsing fails, keep the original string
      // StepCard's formatDate will handle string timestamps
      console.warn("Failed to parse timestamp:", item.timestamp, error);
    }

    // Map old_value/new_value to old_data/new_data for ChangesAccordian compatibility
    const normalizedOldData: Record<string, unknown> | null =
      item.old_value && Object.keys(item.old_value).length > 0
        ? item.old_value
        : null;
    const normalizedNewData: Record<string, unknown> | null =
      item.new_value && Object.keys(item.new_value).length > 0
        ? item.new_value
        : null;

    return {
      id: item.id,
      action: mapAction(item.action),
      object_name: item.entity_name || null,
      actor_name: item.actor_name,
      actor_email: item.actor_email,
      timestamp: item.timestamp,
      old_data: normalizedOldData,
      new_data: normalizedNewData,
      name: item.entity_name || null, // For StepCard compatibility
      updatedAt: parsedTimestamp, // Map timestamp to updatedAt for StepCard
    };
  });

  return {
    success: response.success,
    message: response.message,
    data: normalizedData,
    pagination,
  };
}

/**
 * Raw API response structure for tenant user assignments audit
 */
interface TenantUserAssignmentsAuditApiEntry {
  id: string;
  action: "assigned" | "unassigned";
  user_id: string;
  user_name: string;
  user_email: string;
  actor_name: string;
  actor_email: string;
  timestamp: string;
  old_value: Record<string, unknown> | null;
  new_value: Record<string, unknown> | null;
}

interface TenantUserAssignmentsAuditApiResponse {
  success: boolean;
  message: string;
  data: {
    data: TenantUserAssignmentsAuditApiEntry[];
    pagination: {
      page: number;
      pageSize: number;
      total: number;
      totalPages: number;
    };
  };
  statusCode: number;
  status: boolean;
}

/**
 * Fetch tenant user assignments audit logs with pagination
 */
export async function getTenantUserAssignmentsAudit(
  tenantId: string,
  page = 1,
  pageSize = 20,
): Promise<TenantAuditResponse> {
  // Build URL with pageSize as query parameter
  const baseUrl = API_ROUTES_V2.FETCH_TENANT_USER_ASSIGNMENTS_AUDIT.replace(
    "{id}",
    tenantId,
  ).replace("{page}", page.toString());
  const url = `${baseUrl}&pageSize=${pageSize}`;

  const response = await apiService<TenantUserAssignmentsAuditApiResponse>(
    url,
    "GET",
    {
      replace: {
        id: tenantId,
        page: page.toString(),
      },
    },
  );

  // Extract the nested data structure
  const auditEntries = response.data?.data || [];
  const pagination = response.data?.pagination || {
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0,
  };

  // Map action values from API format to standard format
  const mapAction = (action: string): "create" | "update" | "delete" => {
    if (action === "assigned") return "create";
    if (action === "unassigned") return "delete";
    // Fallback to update if unknown action
    return "update";
  };

  // Normalize the response data structure for StepCard compatibility
  const normalizedData = auditEntries.map((item) => {
    // Parse the timestamp string "6 Jan 2026 | 13:24:05" to a Date object
    // Format: "d MMM yyyy | HH:mm:ss"
    let parsedTimestamp: Date | string = item.timestamp;
    try {
      // Try to parse the timestamp format "6 Jan 2026 | 13:24:05"
      const [datePart, timePart] = item.timestamp.split(" | ");
      if (datePart && timePart) {
        // Combine date and time parts: "6 Jan 2026 13:24:05"
        const dateTimeString = `${datePart} ${timePart}`;
        // Use date-fns parse for consistent cross-browser parsing
        // Format: "d MMM yyyy HH:mm:ss" (e.g., "6 Jan 2026 13:24:05")
        const parsedDate = parse(
          dateTimeString,
          "d MMM yyyy HH:mm:ss",
          new Date(),
        );
        // Check if the date is valid
        if (!Number.isNaN(parsedDate.getTime())) {
          parsedTimestamp = parsedDate;
        } else {
          // If parsing fails, log warning
          console.warn(
            "Failed to parse timestamp, using original:",
            item.timestamp,
          );
        }
      }
    } catch (error) {
      // If parsing fails, keep the original string
      // StepCard's formatDate will handle string timestamps
      console.warn("Failed to parse timestamp:", item.timestamp, error);
    }

    // Map old_value/new_value to old_data/new_data for ChangesAccordian compatibility
    const normalizedOldData: Record<string, unknown> | null =
      item.old_value && Object.keys(item.old_value).length > 0
        ? item.old_value
        : null;
    const normalizedNewData: Record<string, unknown> | null =
      item.new_value && Object.keys(item.new_value).length > 0
        ? item.new_value
        : null;

    return {
      id: item.id,
      action: mapAction(item.action),
      object_name: item.user_name || null,
      actor_name: item.actor_name,
      actor_email: item.actor_email,
      timestamp: item.timestamp,
      old_data: normalizedOldData,
      new_data: normalizedNewData,
      name: item.user_name || null, // For StepCard compatibility
      updatedAt: parsedTimestamp, // Map timestamp to updatedAt for StepCard
    };
  });

  return {
    success: response.success,
    message: response.message,
    data: normalizedData,
    pagination,
  };
}
